import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import PreviewNotice from "@/components/PreviewNotice";
import {
  ChevronLeft, ArrowRight, Watch, Star,
  Briefcase, GraduationCap, Dumbbell, Heart, Users, Palette,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";

// ── Types ──────────────────────────────────────────────────────────────────

type Category = "work" | "learning" | "exercise" | "selfCare" | "social" | "creative";

interface CategoryDef {
  label: Category;
  icon: React.ReactNode;
  color: string;
  todayMins: number;
}

interface RecentSession {
  id: string;
  name: string;
  category: Category;
  duration: string;
  start: string;
  end: string;
}

// ── Static data ────────────────────────────────────────────────────────────

const CATEGORIES: CategoryDef[] = [
  { label: "work",      icon: <Briefcase     size={16} />, color: "hsl(var(--primary))",           todayMins: 200 },
  { label: "learning",  icon: <GraduationCap size={16} />, color: "hsl(220 55% 55%)",  todayMins: 70  },
  { label: "exercise",  icon: <Dumbbell      size={16} />, color: "hsl(142 55% 50%)",  todayMins: 45  },
  { label: "selfCare",  icon: <Heart         size={16} />, color: "hsl(265 60% 60%)",  todayMins: 30  },
  { label: "social",    icon: <Users         size={16} />, color: "hsl(45 80% 55%)",   todayMins: 0   },
  { label: "creative",  icon: <Palette       size={16} />, color: "hsl(330 60% 55%)",  todayMins: 0   },
];

const RECENT_SESSIONS: RecentSession[] = [
  { id: "1", name: "Finish project proposal",  category: "work",     duration: "1h 20m", start: "09:00", end: "10:20" },
  { id: "2", name: "React Advanced Course",    category: "learning", duration: "45m",    start: "10:30", end: "11:15" },
  { id: "3", name: "Team sync + feedback",     category: "work",     duration: "1h 00m", start: "14:00", end: "15:00" },
  { id: "4", name: "Evening run",              category: "exercise", duration: "45m",    start: "18:00", end: "18:45" },
];

const WEEK_BREAKDOWN: { cat: Category; pct: number; total: string }[] = [
  { cat: "work",      pct: 58, total: "27h 20m" },
  { cat: "learning",  pct: 18, total: "8h 30m"  },
  { cat: "exercise",  pct: 12, total: "5h 40m"  },
  { cat: "selfCare",  pct: 7,  total: "3h 15m"  },
  { cat: "social",    pct: 3,  total: "1h 22m"  },
  { cat: "creative",  pct: 2,  total: "1h 16m"  },
];

const TOTAL_TODAY_MINS = CATEGORIES.reduce((s, c) => s + c.todayMins, 0);

function formatMins(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ""}`.trim() : `${m}m`;
}

function formatTimer(secs: number) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// ── Page ───────────────────────────────────────────────────────────────────

type TimerState = "idle" | "running" | "paused";

const TimeTracker: React.FC = () => {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [sessionName,    setSessionName]    = useState("");
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [timerState,     setTimerState]     = useState<TimerState>("idle");
  const [elapsed,        setElapsed]        = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (timerState === "running") {
      intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timerState]);

  const handleStart = () => {
    if (!activeCategory) {
      toast({ description: t("timeTracker.toast.selectCategory"), duration: 2000 });
      return;
    }
    setElapsed(0);
    setTimerState("running");
  };

  const handleStop = () => {
    setTimerState("idle");
    setElapsed(0);
    setSessionName("");
    setActiveCategory(null);
    toast({ description: t("ux.preview.notSaved"), duration: 2000 });
  };

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.08, duration: 0.5 });
  const isActive = timerState !== "idle";

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <PreviewNotice />
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("timeTracker.subtitle")}</p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("timeTracker.title")}
          </h1>
        </div>
      </motion.div>

      {/* TIMER CARD */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(1)}
        className="rounded-2xl p-5 mb-4 relative overflow-hidden"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

        {!isActive ? (
          <>
            {/* Session input */}
            <input
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder={t("timeTracker.sessionPlaceholder")}
              className="w-full bg-transparent font-display text-[15px] font-semibold text-foreground placeholder:text-foreground/20 focus:outline-none mb-4 border-b pb-2"
              style={{ borderColor: "hsl(187 72% 50% / 0.18)" }}
            />

            {/* Category grid */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {CATEGORIES.map((cat) => {
                const isSelected = activeCategory === cat.label;
                return (
                  <button
                    key={cat.label}
                    onClick={() => setActiveCategory(cat.label)}
                    className="flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all duration-200 active:scale-[0.96]"
                    style={
                      isSelected
                        ? { background: `${cat.color}22`, border: `1.5px solid ${cat.color}88`, color: cat.color }
                        : { background: "hsl(218 14% 14%)", border: "1px solid hsl(218 14% 22%)", color: "hsl(218 14% 55%)" }
                    }
                  >
                    <span style={{ color: isSelected ? cat.color : "hsl(218 14% 50%)" }}>{cat.icon}</span>
                    <span className="font-display text-[9px] tracking-wide">{t(`timeTracker.cat.${cat.label}`)}</span>
                  </button>
                );
              })}
            </div>

            {/* Start button */}
            <button
              onClick={handleStart}
              className="w-full py-3 rounded-xl font-display text-[13px] font-bold tracking-wide transition-all duration-200 active:scale-[0.98]"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}
            >
              {t("timeTracker.startTracking")}
            </button>
          </>
        ) : (
          <>
            {/* Active timer */}
            <div className="text-center mb-4">
              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2"
                style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                {t(`timeTracker.cat.${activeCategory}`)} · {sessionName || t("timeTracker.untitledSession")}
              </p>
              <p className="font-display text-[52px] font-bold leading-none"
                style={{ color: timerState === "running" ? "#00e5ff" : "hsl(218 14% 55%)" }}>
                {formatTimer(elapsed)}
              </p>
              {timerState === "paused" && (
                <p className="text-[10px] font-display tracking-wide mt-1" style={{ color: "hsl(45 80% 55%)" }}>
                  {t("timeTracker.paused")}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setTimerState(timerState === "running" ? "paused" : "running")}
                className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 60%)" }}
              >
                {timerState === "running" ? t("timeTracker.pause") : t("timeTracker.resume")}
              </button>
              <button
                onClick={handleStop}
                className="flex-1 py-2.5 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "hsl(0 55% 35% / 0.18)", border: "1px solid hsl(0 55% 45% / 0.30)", color: "hsl(0 60% 60%)" }}
              >
                {t("ux.preview.finish")}
              </button>
            </div>
          </>
        )}
      </motion.div>

      {/* TODAY */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] font-display tracking-[0.16em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("timeTracker.today")}</p>
          <p className="font-display text-[13px] font-bold" style={{ color: "hsl(187 72% 60%)" }}>
            {formatMins(TOTAL_TODAY_MINS)}
          </p>
        </div>

        <div className="space-y-2.5">
          {CATEGORIES.filter((c) => c.todayMins > 0).map((cat) => {
            const pct = Math.round((cat.todayMins / TOTAL_TODAY_MINS) * 100);
            return (
              <div key={cat.label}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span style={{ color: cat.color }}>{cat.icon}</span>
                    <span className="font-display text-[11px] font-medium text-foreground/80">{t(`timeTracker.cat.${cat.label}`)}</span>
                  </div>
                  <span className="font-display text-[11px] font-semibold" style={{ color: cat.color }}>
                    {formatMins(cat.todayMins)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: cat.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* THIS WEEK */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(3)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] font-display tracking-[0.16em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("timeTracker.thisWeek")}</p>
          <p className="font-display text-[13px] font-bold" style={{ color: "hsl(187 72% 60%)" }}>
            47h 23min
          </p>
        </div>

        <div className="space-y-2">
          {WEEK_BREAKDOWN.map((row) => {
            const cat = CATEGORIES.find((c) => c.label === row.cat)!;
            return (
              <div key={row.cat} className="flex items-center gap-2">
                <span className="shrink-0" style={{ color: cat.color }}>{cat.icon}</span>
                <div className="flex-1">
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${row.pct}%` }}
                      transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{ background: cat.color }}
                    />
                  </div>
                </div>
                <span className="font-display text-[10px] font-semibold w-[45px] text-right shrink-0"
                  style={{ color: "hsl(210 20% 60%)" }}>
                  {row.pct}%
                </span>
                <span className="font-body text-[10px] w-[52px] text-right shrink-0"
                  style={{ color: "hsl(218 14% 45%)" }}>
                  {row.total}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* RECENT SESSIONS */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(4)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[10px] font-display tracking-[0.16em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("timeTracker.recentSessions")}</p>
        <div className="space-y-3">
          {RECENT_SESSIONS.map((s) => {
            const cat = CATEGORIES.find((c) => c.label === s.category)!;
            return (
              <div key={s.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${cat.color}18`, border: `1px solid ${cat.color}30` }}>
                  <span style={{ color: cat.color }}>{cat.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[12px] font-semibold text-foreground/85 leading-snug">{s.name}</p>
                  <p className="text-[10px] font-body" style={{ color: "hsl(218 14% 45%)" }}>
                    {s.start} – {s.end}
                  </p>
                </div>
                <span className="font-display text-[11px] font-bold shrink-0" style={{ color: cat.color }}>
                  {s.duration}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* MIRROR INSIGHT */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(5)}
        className="rounded-2xl p-4 mb-4 relative overflow-hidden"
        style={{ background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_09.jpeg) center/cover no-repeat", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
            <Star size={14} style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("timeTracker.mirrorInsight")}</p>
            <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
              {t("timeTracker.mirrorInsightText")}
            </p>
            <button
              onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("timeTracker.scheduleWithMirror")}</span>
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </motion.div>
      )}

      {/* CONNECT WEARABLE */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(6)}
        className="rounded-2xl p-4 mb-5 flex items-center gap-3"
        style={{ background: "hsl(218 14% 12% / 0.6)", border: "1px solid hsl(var(--border) / 0.4)" }}
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.16)" }}>
          <Watch size={16} style={{ color: "hsl(187 72% 50% / 0.55)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-body leading-snug mb-2" style={{ color: "hsl(218 14% 55%)" }}>
            {t("timeTracker.connectWearableText")}
          </p>
          <button disabled
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-display text-[11px] font-medium tracking-wide opacity-35 cursor-not-allowed"
            style={{ border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 50%)" }}>
            <Watch size={12} />
            {t("timeTracker.connectWearableBtn")}
          </button>
          <p className="text-[10px] font-display tracking-wide mt-1" style={{ color: "hsl(218 14% 35%)" }}>
            {t("timeTracker.comingSoon")}
          </p>
        </div>
      </motion.div>

    </MobileLayout>
  );
};

export default TimeTracker;
